import { Box, Button, IconButton, Stack } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { observer } from 'mobx-react'
import React, { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import FormCheckbox from '../../../../components/Form/FormCheckbox'
import FormField from '../../../../components/Form/FormField'
import { getImageSrc } from '../../../../helpers/getImageSrc'
import { placeNames } from '../../../../helpers/globalVariables'
import { isOperator } from '../../../../services/AccountService'
import { getComp } from '../../../../utils/utils/DI'
import FormAutocomplete from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormAutocomplete'
import FormSelect from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormSelect'
import TypedAccordion from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/TypedAccordion'
import { getCollections } from '../../../SingleRequestPage/components/SingleRequestDrawer/helpers/getCollections'
import { BaseOptionType } from '../../../SingleRequestPage/components/SingleRequestDrawer/helpers/getFormSchema'
import ContentTitle from './components/ContentTitle'
import DrawerHeader from './components/DrawerHeader'
import { defaultValues, resolver } from './consts/createDrawer.consts'
import { contentWrapperSx } from './consts/styles.consts'
import { createServiceRequestStore } from './stores/NewCreateServiceRequestStore'
import { CreateServiceRequestFrom } from './types/NewDrawers.types'

interface NewCreateRequestFormDrawerProps {}

const NewCreateRequestFormDrawer: FC<
  NewCreateRequestFormDrawerProps
> = ({}) => {
  const {
    isCreateDrawerOpen,
    setIsCreateDrawerOpen,
    setSearchParams,
    setIsShowAllRequestsDrawerOpen,
    setIsResultsDrawerOpen,
    selectedRequest,
    setSelectedRequest,
    setCreateFormData,
    setIsDiagnosticDrawerOpen,
    setSenderId,
    resetAll,
    setResetAll,
    setActiveServiceOrder,
  } = createServiceRequestStore
  const territoryStore = getComp('TerritoryStore')
  const { unFormatCategories } = getComp('CategoriesStore')
  const { shownSenders, defaultSenders } = getComp('SendersStore')
  const [prevTerritory, setPrevTerritory] = useState('')
  const [prevDistrict, setPrevDistrict] = useState('')

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
    reset,
    setValue,
  } = useForm<CreateServiceRequestFrom>({
    mode: 'onChange',
    defaultValues,
    resolver,
  })

  useEffect(() => {
    if (
      defaultSenders &&
      shownSenders.find((sender: any) => sender.id === defaultSenders)
    ) {
      setValue(
        'senderId',
        shownSenders.find((sender: any) => sender.id === defaultSenders)
          ?.name ?? '',
      )
    }
  }, [defaultSenders])

  const territory = watch('territory')
  const district = watch('district')
  const locality = watch('locality')

  const { districtOptions, localitiesOptions, categoriesItems } =
    getCollections(territory, district, locality, -1)

  const onReset = () =>
    reset(
      selectedRequest
        ? {
            ...selectedRequest,
            preferentialCategories: unFormatCategories(
              selectedRequest?.preferentialCategories,
            ),
            name: '',
            surname: '',
            patronymic: '',
            phoneNumber: '',
            isRepeatedAppeal: false,
          }
        : {
            ...defaultValues,
            senderId:
              (defaultSenders &&
                shownSenders.find((sender: any) => sender.id === defaultSenders)
                  ?.name) ??
              '',
          },
    )

  const onSubmit = (data: CreateServiceRequestFrom) => {
    setCreateFormData({ ...data, isRepeatedAppeal: watch('isRepeatedAppeal') })
    setIsCreateDrawerOpen(false)
    setIsDiagnosticDrawerOpen(true)
  }

  const onClose = () => {
    setIsCreateDrawerOpen(false)
    setSelectedRequest(null)
    setSearchParams({})
    setActiveServiceOrder(null)
    setResetAll()
  }

  useEffect(() => {
    if (resetAll) {
      onReset()
    }
  }, [resetAll])

  const onBack = () => {
    setIsCreateDrawerOpen(false)

    if (selectedRequest) {
      setIsShowAllRequestsDrawerOpen(true)
    } else {
      setIsResultsDrawerOpen(true)
    }

    reset(defaultValues)
  }

  useEffect(() => {
    if (selectedRequest && isCreateDrawerOpen) {
      const newDefaultValues: CreateServiceRequestFrom =
        {} as CreateServiceRequestFrom

      setPrevTerritory(selectedRequest?.territory ?? defaultValues?.territory)
      setPrevDistrict(selectedRequest?.district ?? defaultValues?.district)

      for (let key in defaultValues) {
        const prop = key as keyof CreateServiceRequestFrom

        if (key === 'preferentialCategories') {
          // @ts-ignore
          newDefaultValues[prop] = unFormatCategories(
            selectedRequest?.preferentialCategories,
          )
        } else if (
          ['name', 'surname', 'patronymic', 'phoneNumber'].includes(key)
        ) {
          // @ts-ignore
          newDefaultValues[prop] = defaultValues[key]
        } else {
          // @ts-ignore
          newDefaultValues[prop] = selectedRequest[key] ?? defaultValues[key]
        }
      }

      reset(newDefaultValues)
    }
  }, [selectedRequest, isCreateDrawerOpen])

  useEffect(() => {
    const currentSenderId = shownSenders?.find(
      (el: any) => el.name === watch('senderId'),
    )

    if (currentSenderId) {
      setSenderId(currentSenderId?.id)
    }
  }, [watch('senderId')])

  const onCategoriesChange = (value: BaseOptionType) => {
    const currValue = watch('preferentialCategories')
    let lastIndex = categoriesItems.length - 1

    const filteredValue = currValue.filter((i, idx) => {
      return (idx === value.key && !i) || (!!i && idx !== value.key)
    })

    const newValue = currValue.map((item, index) => {
      // если нет выбраных значений выбераем "не выбрано" иначе если не нажали "не выбрано" ставим false
      const lastValue = !filteredValue.length ? true : value.key === lastIndex
      const currItem = index === value.key ? !item : item
      // если нажали "не выбрано" сбрасываем все другие значения
      const itemResetChecked = value.key === lastIndex ? false : currItem

      return index === lastIndex ? lastValue : itemResetChecked
    })

    setValue('preferentialCategories', newValue)
  }

  const isEquipmentIdEmpty = watch('equipmentId') === 'Не заполнен'

  useEffect(() => {
    if (prevTerritory !== territory) {
      setValue('district', '')
      setValue('locality', '')
      setPrevTerritory(territory)
    }
    if (prevDistrict !== district) {
      setValue('locality', '')
      setPrevDistrict(district)
    }
  }, [territory, district])

  const isCategoriesAdded = !watch('preferentialCategories')[
    watch('preferentialCategories')?.length - 1
  ]

  return (
    <Drawer
      anchor={'right'}
      open={isCreateDrawerOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
        },
      }}
    >
      <DrawerHeader
        title={`Заявка на ${!isOperator() ? 'обслуживание' : 'ремонт'}`}
        onBack={onBack}
        onClose={onClose}
      />
      <Stack
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{ ...contentWrapperSx }}
        spacing={'14px'}
        justifyContent={'space-between'}
      >
        <Stack spacing={'14px'}>
          <ContentTitle
            title='Шаг 2. Ввод и проверка данных Абонента'
            styles={{ marginX: '0!important' }}
          />
          <Stack
            spacing={'14px'}
            paddingX={'16px'}
            marginTop={'16px !important'}
            marginBottom={'16px !important'}
          >
            <Box
              position={'relative'}
              sx={{
                img: {
                  position: 'absolute',
                  right: '20px',
                  top: '27%',
                },
              }}
            >
              <FormField
                control={control}
                name='equipmentId'
                fullWidth
                disabled
                label='ID оборудования'
                onlyNumbers
              />
              {isEquipmentIdEmpty && <img src={getImageSrc('warn')} />}
            </Box>
            <FormCheckbox
              label='Срочная заявка'
              control={control}
              name='isUrgent'
              withFireIcon
            />
            <FormCheckbox
              label='Повторное обращение'
              control={control}
              name='isRepeatedAppeal'
            />
          </Stack>
          <TypedAccordion summary={'Данные абонента'} customOpen>
            <Stack paddingTop={'8px'} gap={'14px'}>
              <FormField
                control={control}
                name='surname'
                fullWidth
                label='Фамилия'
                error={!watch('surname').trim()}
                capitalizeFirstLetter
              />
              <FormField
                control={control}
                name='name'
                fullWidth
                label='Имя'
                error={!watch('name').trim()}
                capitalizeFirstLetter
              />
              <FormField
                control={control}
                name='patronymic'
                fullWidth
                label='Отчество'
                error={!watch('patronymic').trim()}
                capitalizeFirstLetter
              />
              <FormField
                control={control}
                name='phoneNumber'
                fullWidth
                label='Телефон'
                mask={'+7 (999)-999-99-99'}
                error={!watch('phoneNumber')}
              />
              <FormAutocomplete
                control={control}
                fullWidth
                name='territory'
                options={territoryStore.collection as string[]}
                withoutTyping
                label='Регион'
                error={!watch('territory')}
              />
              <FormAutocomplete
                control={control}
                fullWidth
                name='district'
                options={districtOptions}
                label='Округ'
                freeSolo
                disabled={!watch('territory')}
                error={
                  !!watch('territory') &&
                  (!watch('district') || !districtOptions?.includes(district))
                }
              />
              <FormAutocomplete
                control={control}
                fullWidth
                name='locality'
                options={localitiesOptions}
                label='Населённый пункт'
                fullDotationRegions={[]}
                freeSolo
                disabled={!watch('district')}
                error={
                  !!watch('district') &&
                  (!watch('locality') || !localitiesOptions?.includes(locality))
                }
              />
              <FormAutocomplete
                control={control}
                fullWidth
                name='addressType'
                options={placeNames}
                freeSolo
                label='Топоним'
              />

              <FormField
                control={control}
                name='address'
                fullWidth
                label='Наименование топонима (улицы / переулка и т.д.) '
                error={!watch('address').trim()}
              />
              <Stack direction={'row'} spacing={'10px'} maxWidth={'100%'}>
                <FormField
                  control={control}
                  fullWidth
                  name='house'
                  label='Дом'
                  onlyNumbers
                />
                <FormField
                  control={control}
                  fullWidth
                  name='building'
                  label='Корпус'
                  onlyNumbersAndLetters
                />

                <FormField
                  control={control}
                  fullWidth
                  name='apartment'
                  label='Квартира'
                  onlyNumbers
                />
              </Stack>
              <Box position={'relative'}>
                <FormSelect
                  control={control}
                  fullWidth
                  items={categoriesItems}
                  name='preferentialCategories'
                  label='Льготная категория'
                  multiple
                  onChange={onCategoriesChange}
                  disableDefaultOnChange
                  withLabel
                  width={'340px'}
                />
                {isCategoriesAdded && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: '15%',
                      right: '35px',
                      padding: '4px',
                    }}
                    onClick={() =>
                      setValue(
                        'preferentialCategories',
                        defaultValues.preferentialCategories,
                      )
                    }
                  >
                    <img src={getImageSrc('close')} style={{ width: '18px' }} />
                  </IconButton>
                )}
              </Box>
              <FormAutocomplete
                control={control}
                fullWidth
                name='senderId'
                withoutTyping
                options={shownSenders.map((s: any) => s.name)}
                error={!watch('senderId')}
                label='Источник данных о заявке'
              />
              <FormField
                control={control}
                name='additionalInfo'
                fullWidth
                placeholder='Дополнительная информация / краткое описание причины обращения'
                label='Дополнительная информация'
                multiline
                rows={3}
              />
            </Stack>
          </TypedAccordion>
        </Stack>
        <Stack spacing={'14px'} paddingX={'16px'}>
          <Button type='submit' disabled={!isValid} variant='contained'>
            Продолжить
          </Button>
          <Button variant='outlined' onClick={onBack}>
            <img
              alt={'Назад'}
              src={getImageSrc('arrow-left-blue')}
              style={{ marginRight: '4px' }}
            />
            Назад
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default observer(NewCreateRequestFormDrawer)
